import React from 'react';
import styled from 'styled-components';

import { BodyText, BodyTextLarge, BodyTextSmall, FlexCol, FlexDynamic, FlexRow } from '@minecraft.atoms';
import { CoreTheme } from '@minecraft.themes';
import { FilterValues } from '@blocs.filter';
import { SimplePagination, usePagination, CheckboxInput } from '@minecraft.molecules';
import {
  ProfileSearchInput,
  TalentScoutProfileFragment,
  TalentScoutSearchProfilesDoc,
} from '@minecraft.graphql-operations';
import { useQuery } from '@apollo/client';
import { Trans, useTranslation } from '@blocs.i18n';

import { useShowSocialLinks } from '@blocs.common';
import TalentCards from './TalentCards';
import { Banner } from './Banner';
import { Filter } from './Filter';
import { getMappedFilterQuery } from './getMappedFilterQuery';

const SHOW_TALENT_SCOUT_SOCIAL_LINKS_KEY = 'SHOW_TALENT_SCOUT_SOCIAL_LINKS_KEY';

/**
 * This hook is used to query for and filter talent profiles
 *
 * @param pageNumber query parameter that indicates which page should be queried
 * @param pageSize query parameter that indicated how many results there should be per page
 */
export const useTalentScoutSearchProfiles = ({ pageNumber, pageSize }: ReturnType<typeof usePagination>) => {
  const [profileSearchInput, setProfileSearchInput] = React.useState<ProfileSearchInput>({});
  const results = useQuery(TalentScoutSearchProfilesDoc, {
    variables: {
      page: { pageNumber, pageSize },
      input: profileSearchInput,
    },
    fetchPolicy: 'network-only',
  });

  return { setProfileSearchInput, ...results };
};

/**
 * This hook is used to transform the current state of the TalentScoutFilter into a ProfileSearchInput query parameter
 *
 * @param setProfileSearchInput callback used to set the value of the ProfileSearchInput
 */
export const useOnFilterChange = (setProfileSearchInput: (profileSearchInput: ProfileSearchInput) => void) =>
  React.useCallback(
    (filter: FilterValues) => {
      const mappedValues = getMappedFilterQuery(filter);
      setProfileSearchInput?.(mappedValues);
    },
    [setProfileSearchInput]
  );

const StyledTalentScoutControlsSection = styled.div`
  display: flex;
  flex-direction: row-reverse;
  padding: 0.5rem;

  label {
    display: flex;
    gap: 0.25rem;
    height: 1rem;
    line-height: 1rem;
  }
`;

const TermsOfServiceLink = () => {
  const { t } = useTranslation();

  return (
    <a href="https://www.castingnetworks.com/terms" target="_blank" rel="noopener noreferrer">
      {t('common:label.termsOfUse')}
    </a>
  );
};

const TosBanner = () => (
  <FlexRow
    bgColor="impactedServiceAlert"
    justifyContent="center"
    className="cn_atom_p-3"
    data-testid="talent-scout-tos-banner"
  >
    <BodyText testId="talent-scout-tos">
      <Trans i18nKey="common:warning.noTalentSolicitation" components={[<TermsOfServiceLink />]} />
    </BodyText>
  </FlexRow>
);

const NoResults = (props: any) => {
  const { t } = useTranslation();

  return (
    <div {...props}>
      <FlexCol className="cn_m-4" gap="3">
        <BodyTextLarge fontWeight="bold">
          <span
            style={{
              // This is a blue that is not part of the design system but is expected by product and design.
              color: CoreTheme.colors.accent.dkBlue2,
            }}
          >
            {t('reps:filter.talent.notFound.title')}
          </span>
        </BodyTextLarge>
        <BodyTextSmall>{t('reps:filter.talent.notFound.message')}</BodyTextSmall>
      </FlexCol>
    </div>
  );
};

type TalentSearchContentProps = ReturnType<typeof usePagination> & {
  loading: boolean;
  profiles: TalentScoutProfileFragment[];
  totalCount: number;
};

const TalentSearchContent = ({
  loading,
  profiles,
  pageSize,
  setPageSize,
  setPageNumber,
  pageNumber,
  totalCount,
}: TalentSearchContentProps) => {
  const { t } = useTranslation();
  const [showPitchNotesAndRepTypeInfo, setShowPitchNotesAndRepTypeInfo] = React.useState(false);
  const { showSocialLinks, displaySocialMediaSection, onChangeDisplaySocialMediaSection } = useShowSocialLinks({
    storageKey: SHOW_TALENT_SCOUT_SOCIAL_LINKS_KEY,
  });

  if (loading) {
    return <TalentCards data-testid="profiles-loading" loading />;
  }

  if (profiles.length === 0) {
    return <NoResults data-testid="profiles-noresults" />;
  }

  return (
    <>
      <FlexDynamic
        className="cn_w-full"
        wrap="nowrap"
        direction="column"
        gap="6"
        smProps={{ direction: 'row', justifyContent: 'flex-end', alignItems: 'center' }}
      >
        {showSocialLinks && (
          <CheckboxInput
            data-testid="show-social-media-section-checkbox"
            checked={displaySocialMediaSection}
            onChange={onChangeDisplaySocialMediaSection}
            label={t('common:socialLinking.showSocialCheckbox.label')}
            value="displaySocialMediaSection"
          />
        )}
        <CheckboxInput
          data-testid="show-notes-and-rep-info"
          disabled={loading}
          checked={showPitchNotesAndRepTypeInfo}
          label={t('reps:talentScout.showPitchNotes')}
          name="show-notes-and-rep-info"
          onChange={() => setShowPitchNotesAndRepTypeInfo(!showPitchNotesAndRepTypeInfo)}
          value="showPitchNotesAndRepTypeInfo"
        />
      </FlexDynamic>
      <SimplePagination
        pageNumber={pageNumber}
        pageSize={pageSize}
        setPageNumber={setPageNumber}
        setPageSize={setPageSize}
        totalRecords={totalCount}
      >
        <TalentCards
          data-testid="profiles"
          profiles={profiles}
          showPitchNotesAndRepTypeInfo={showPitchNotesAndRepTypeInfo}
          displaySocialMediaSection={displaySocialMediaSection}
        />
      </SimplePagination>
    </>
  );
};

/**
 * This component contains the TalentSearch feature for TalentScout
 */
export const TalentSearch = () => {
  const pagination = usePagination();
  const { setProfileSearchInput, data, loading } = useTalentScoutSearchProfiles(pagination);
  const onFilterChange = useOnFilterChange(setProfileSearchInput);

  return (
    <FlexCol alignItems="stretch" data-testid="talent-search-wrapper">
      <TosBanner />
      <Banner />
      <FlexCol className="cn_mx-4" alignContent="center">
        <Filter
          onFilterChange={(filters) => {
            pagination.setPageNumber(1);
            onFilterChange(filters);
          }}
        />
        <div className="cn_w-full">
          <TalentSearchContent
            loading={loading}
            profiles={data?.talentScoutSearch.profiles ?? []}
            totalCount={data?.talentScoutSearch.totalCount ?? 0}
            {...pagination}
          />
        </div>
      </FlexCol>
    </FlexCol>
  );
};
